import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';

import AnalyticsEvents from '@/analytics/AnalyticsEvents';
import { useVisibilityTracking } from '@/analytics/useVisibilityTracking';
import { isWorkshopParentFrontend } from '@/config';
import { useGetVehicleWarningsQuery } from '@/services/warningsApi';
import InternalErrorState from '../app/InternalErrorState';
import { LoadingIndicator } from '../app/LoadingIndicator';
import Footer from './Footer';
import Header from './Header';
import List from './List';
import StatusLegend from './StatusLegend';
import SummaryBar from './SummaryBar';
import { Criticality } from './utils/types';
import { useReportWarnings } from './utils/useReportWarnings';

const FIRST_VIEW_LIMIT = 5;
const EXPANDED_VIEW_LIMIT = 10;

const WarningWrapper = () => {
    const { assetId } = useParams<{ assetId: string }>();
    const [activeFilters, setActiveFilters] = useState<Criticality[]>([]);

    useReportWarnings(assetId);

    const isExpanded = new URLSearchParams(window.location.search).get('expanded') === 'true';
    const criticalityFilter = activeFilters.length > 0 ? activeFilters : undefined;

    const { data, isLoading, isError, refetch } = useGetVehicleWarningsQuery({
        assetId: assetId ?? '',
        limit: isExpanded ? EXPANDED_VIEW_LIMIT : FIRST_VIEW_LIMIT,
        offset: 0,
        criticality: criticalityFilter,
    });

    const handleComponentVisible = useCallback(() => {
        const elementState = data ? (data.total_items === 0 ? 'empty' : 'active') : 'no_data';
        if (!isWorkshopParentFrontend()) {
            AnalyticsEvents.sectionViewVehicleDiagnosticRepairWarnings(elementState);
        }
    }, [data]);
    const containerRef = useVisibilityTracking<HTMLDivElement>(handleComponentVisible, {
        threshold: 0.5,
        dwellTime: 1000,
    });

    const hasNoWarnings = !isLoading && (!data || data.total_items === 0);

    const handleFilterToggle = (criticality: Criticality) => {
        setActiveFilters((prev) => {
            if (prev.includes(criticality)) {
                if (isWorkshopParentFrontend()) {
                    AnalyticsEvents.filterRemoveWearTearRepairWarnings(criticality);
                } else {
                    AnalyticsEvents.filterRemoveVehicleDiagnostic(criticality);
                }
                return prev.filter((c) => c !== criticality);
            }
            if (isWorkshopParentFrontend()) {
                AnalyticsEvents.filterApplyWearTearRepairWarnings(criticality);
            } else {
                AnalyticsEvents.filterApplyVehicleDiagnostic(criticality);
            }
            return [...prev, criticality];
        });
    };

    const handleFilterReset = () => {
        if (isWorkshopParentFrontend()) {
            AnalyticsEvents.filterResetWearTearRepairWarnings();
        } else {
            AnalyticsEvents.filterResetVehicleDiagnostic();
        }
        setActiveFilters([]);
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    if (isError) {
        return <InternalErrorState reloadTriggerFunction={refetch} />;
    }

    return (
        <>
            <div
                ref={containerRef}
                className={`bg-white display-flex flex-column gap-10 ${
                    isExpanded ? '' : 'padding-x-20 border border-bottom-none rounded-top'
                }`}
            >
                {!isExpanded ? <Header /> : <StatusLegend />}

                {hasNoWarnings ? (
                    <div className="display-flex align-items-center gap-10 padding-y-15 border border-top-only">
                        <span className="uikit-icon icon-ok text-size-18" />
                        <span className="text-size-14 text-medium">
                            <FormattedMessage id="intl-msg:vehicle-warnings.noWarnings" />
                        </span>
                    </div>
                ) : (
                    <>
                        <SummaryBar
                            assetId={assetId ?? ''}
                            activeFilters={activeFilters}
                            onFilterToggle={handleFilterToggle}
                            onFilterReset={handleFilterReset}
                            isExpanded={isExpanded}
                        />
                        <List
                            assetId={assetId ?? ''}
                            initialWarnings={data?.items || []}
                            total={data?.total_items || 0}
                            isLoading={isLoading}
                            isExpanded={isExpanded}
                            criticalityFilter={criticalityFilter}
                        />
                    </>
                )}
            </div>

            {!isExpanded && <Footer disabled={hasNoWarnings} />}
        </>
    );
};

export default WarningWrapper;
